import type { TypedDocumentNode } from '@apollo/client';
import { isFuture } from 'date-fns';
import gql from 'graphql-tag';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { StepState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconClock } from '@sorare/core/src/atoms/icons/system/iconClock.ts';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { iconHourglass } from '@sorare/core/src/atoms/icons/system/iconHourglass.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { BrandXS } from '@sorare/core/src/atoms/typography/index.tsx';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';

import type {
  SquadBadge_squadBoard,
  SquadBadge_squadStep,
} from './__generated__/index.graphql.ts';

const Root = styled(Horizontal).attrs({ center: true })`
  gap: 2px;
  padding: var(--unit);
  backdrop-filter: blur(16px);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-and-a-half-unit);
  background: var(--c-nl-500);
`;

type Props = {
  squadBoard: SquadBadge_squadBoard;
  step: SquadBadge_squadStep;
};
export const SquadBadge = ({ squadBoard, step }: Props) => {
  const { state } = step;
  const { nextMatchdayAt, lastUpcomingMatchdayGame } = squadBoard;

  if (state === StepState.CLAIMABLE) {
    return (
      <Root>
        <FontAwesomeIcon icon={iconGift} size="xs" />
        <BrandXS>
          <FormattedMessage defaultMessage="Claim Reward" />
        </BrandXS>
      </Root>
    );
  }

  if (state === StepState.PRE_MATCHDAY_LOCKED && !!nextMatchdayAt) {
    return (
      <Root>
        <FontAwesomeIcon icon={iconHourglass} size="xs" />
        <BrandXS>
          <FormattedMessage
            defaultMessage="Available in {timeLeft}"
            values={{
              timeLeft: <TimeLeft time={nextMatchdayAt} />,
            }}
          />
        </BrandXS>
      </Root>
    );
  }

  if (
    (state === StepState.PLAYABLE ||
      state === StepState.LINEUP_SET ||
      state === StepState.LIVE) &&
    !!lastUpcomingMatchdayGame?.date &&
    isFuture(lastUpcomingMatchdayGame.date)
  ) {
    return (
      <Root>
        <FontAwesomeIcon icon={iconClock} size="xs" />
        <BrandXS>
          <FormattedMessage
            defaultMessage="Last game {timeLeft}"
            values={{
              timeLeft: <TimeLeft time={lastUpcomingMatchdayGame.date} />,
            }}
          />
        </BrandXS>
      </Root>
    );
  }

  return null;
};

SquadBadge.fragments = {
  squadBoard: gql`
    fragment SquadBadge_squadBoard on SquadBoard {
      id
      nextMatchdayAt
      lastUpcomingMatchdayGame {
        id
        date
      }
    }
  ` as TypedDocumentNode<SquadBadge_squadBoard>,
  squadStep: gql`
    fragment SquadBadge_squadStep on SquadStep {
      id
      state
    }
  ` as TypedDocumentNode<SquadBadge_squadStep>,
};
